import type { CampaignType, SearchResult, TemplateType } from '@/services/auth/types';
import type { ProductTaxonomy } from '@/services/product-taxonomy';
import { TEMPLATE_TYPE_LABELS } from '../constants';

export type GroupType =
  | Exclude<SearchResult['kind'], 'template'>
  | Exclude<TemplateType, 'unset'>
  | Extract<CampaignType, 'triggered_broadcast'>;

type SearchResultKindDescriptor = {
  kind: SearchResult['kind'];
  heading: string;
  hasQuickLookPreview: boolean;
};

export const SEARCH_RESULT_KIND_METADATA = {
  customer: { heading: 'People', hasQuickLookPreview: true },
  campaign: { heading: 'Campaigns', hasQuickLookPreview: true },
  segment: { heading: 'Segments', hasQuickLookPreview: true },
  action: { heading: 'Workflow Actions', hasQuickLookPreview: true },
  navigation: { heading: 'Navigation', hasQuickLookPreview: false },
  newsletter: { heading: 'Newsletters', hasQuickLookPreview: true },
  goal: { heading: 'Goals', hasQuickLookPreview: true },
  transactional_message: { heading: 'Transactional Messages', hasQuickLookPreview: true },
  anonymous_in_app: { heading: 'Anonymous In-App Messages', hasQuickLookPreview: true },
  snippet: { heading: 'Snippets', hasQuickLookPreview: true },
  email_layout: { heading: 'Email Layouts', hasQuickLookPreview: true },
  collection: { heading: 'Collections', hasQuickLookPreview: true },
  docs: { heading: 'Documentation', hasQuickLookPreview: true },
  source: { heading: 'Sources', hasQuickLookPreview: true },
  destination: { heading: 'Destinations', hasQuickLookPreview: true },
  ad_audience: { heading: 'Ad Audiences', hasQuickLookPreview: false },
  custom_form: { heading: 'Forms', hasQuickLookPreview: true },
  reporting_webhook: { heading: 'Reporting Webhooks', hasQuickLookPreview: false },
  cdp_out: { heading: 'CDP Event Forwarding', hasQuickLookPreview: false },
  data_out: { heading: 'Data-Out', hasQuickLookPreview: false },
  object_type: { heading: 'Object Types', hasQuickLookPreview: true },
  template: { heading: 'Templates', hasQuickLookPreview: true },
} as const satisfies Record<SearchResult['kind'], Omit<SearchResultKindDescriptor, 'kind'>>;

const KINDS_WITH_DESCRIPTION_FIELD = [
  'campaign',
  'segment',
  'goal',
  'docs',
  'transactional_message',
  'anonymous_in_app',
  'collection',
] as const;
type KindWithDescriptionField = (typeof KINDS_WITH_DESCRIPTION_FIELD)[number];

export function hasDescriptionField(
  item: SearchResult,
): item is Extract<SearchResult, { kind: KindWithDescriptionField }> {
  return (KINDS_WITH_DESCRIPTION_FIELD as readonly string[]).includes(item.kind);
}

export const SEARCH_RESULT_KIND_DESCRIPTORS = Object.entries(SEARCH_RESULT_KIND_METADATA).map(
  ([kind, metadata]) =>
    ({
      kind,
      ...metadata,
    }) as SearchResultKindDescriptor,
);

type DefinedSearchResultKindDescriptor = (typeof SEARCH_RESULT_KIND_DESCRIPTORS)[number];
type GroupableSearchResultKindDescriptor = Exclude<DefinedSearchResultKindDescriptor, { kind: 'template' }>;

function isGroupableSearchResultKindDescriptor(
  descriptor: DefinedSearchResultKindDescriptor,
): descriptor is GroupableSearchResultKindDescriptor {
  return descriptor.kind !== 'template';
}

function buildHeadingRecord<const T extends readonly { kind: string; heading: string }[]>(
  descriptors: T,
): Record<T[number]['kind'], T[number]['heading']> {
  return Object.fromEntries(descriptors.map(({ kind, heading }) => [kind, heading])) as Record<
    T[number]['kind'],
    T[number]['heading']
  >;
}

const SEARCH_RESULT_KIND_HEADINGS = buildHeadingRecord(
  SEARCH_RESULT_KIND_DESCRIPTORS.filter(isGroupableSearchResultKindDescriptor),
);

export const SEARCH_RESULT_HEADINGS = {
  ...SEARCH_RESULT_KIND_HEADINGS,
  triggered_broadcast: 'API Triggered Broadcasts',
  // Template results are grouped by template_type rather than their result kind.
  ...TEMPLATE_TYPE_LABELS,
} as const satisfies Record<GroupType, string>;

export function getResultGroupHeading(kind: GroupType, taxonomy: ProductTaxonomy): string {
  if (kind === 'customer') return taxonomy.term('people.titlePlural');
  if (kind === 'campaign') return taxonomy.term('campaigns.titlePlural');
  if (kind === 'newsletter') return taxonomy.t('nav.broadcasts.newsletters');
  return SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`;
}
